import React, { createContext, useState, useContext } from 'react';
import axios from 'axios';
import { setAuthToken } from '../utils/api';

const base = process.env.REACT_APP_API_URL || '/api';

export const AuthContext = createContext();

const initialToken = localStorage.getItem('token') || null;
let initialUser = null;
try {
  const u = localStorage.getItem('user');
  initialUser = u ? JSON.parse(u) : null;
} catch (e) {
  initialUser = null;
}

// set axios global and api instance header synchronously so children see it on first render
if (initialToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${initialToken}`;
  setAuthToken(initialToken);
}

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    token: initialToken,
    user: initialUser,
    isAuthenticated: !!initialToken
  });

  async function login(identifier, password) {
    try {
      const res = await axios.post(`${base}/auth/login`, { identifier, password });
      const body = res.data || {};
      const token = body.token || body.data?.token || body.data?.accessToken || body.accessToken;
      const user = body.user || body.data?.user || body.data || null;
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user || {}));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setAuthToken(token);
        setAuth({ token, user, isAuthenticated: true });
      }
      return { success: true, token, user, raw: res.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Login failed' };
    }
  }

  async function register(payload) {
    try {
      const res = await axios.post(`${base}/auth/register`, payload);
      if (res.data && res.data.requiresOtp) {
        return { success: true, requiresOtp: true, email: res.data.email, message: res.data.message };
      }
      const body = res.data || {};
      const token = body.token || body.data?.token || body.data?.accessToken || body.accessToken;
      const user = body.user || body.data?.user || body.data || null;
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user || {}));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setAuthToken(token);
        setAuth({ token, user, isAuthenticated: true });
      }
      return { success: true, token, user, raw: res.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Registration failed' };
    }
  }

  async function verifyOtp(email, otp) {
    try {
      const res = await axios.post(`${base}/auth/verify-otp`, { email, otp });
      const body = res.data || {};
      const token = body.token || body.data?.token || body.data?.accessToken || body.accessToken;
      const user = body.user || body.data?.user || body.data || null;
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user || {}));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setAuthToken(token);
        setAuth({ token, user, isAuthenticated: true });
      }
      return { success: true, token, user, raw: res.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'OTP verification failed' };
    }
  }

  async function forgotPassword(email) {
    try {
      const res = await axios.post(`${base}/auth/forgot-password`, { email });
      return { success: true, message: res.data.message };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Forgot password failed' };
    }
  }

  async function resetPassword(email, otp, newPassword) {
    try {
      const res = await axios.post(`${base}/auth/reset-password`, { email, otp, newPassword });
      return { success: true, message: res.data.message };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Reset password failed' };
    }
  }

  function logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setAuthToken(null);
    setAuth({ token: null, user: null, isAuthenticated: false });
  }

  function updateUser(updatedUser) {
    if (!updatedUser) return;

    // Get token from localStorage (in case it was just set by OAuth callback)
    const currentToken = localStorage.getItem('token') || auth.token;

    // Ensure axios headers are set with the current token
    if (currentToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      setAuthToken(currentToken);
    }

    // Update localStorage
    localStorage.setItem('user', JSON.stringify(updatedUser));

    // Force state update with new object reference to trigger re-renders
    setAuth({
      token: currentToken,
      user: { ...updatedUser }, // Create new object reference
      isAuthenticated: true
    });
  }

  return (
    <AuthContext.Provider value={{ ...auth, login, register, verifyOtp, forgotPassword, resetPassword, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
